import { DEV, mimeTypes } from '@/shared/constants';
import { type DriveFile } from '@/shared/DriveTypes';
import { AddLinkOutlined, AddToDrive, CreateNewFolderOutlined } from '@mui/icons-material';
import { Menu, MenuItem } from '@mui/material';
import cx from 'classnames';
import { bindMenu, type PopupState } from 'material-ui-popup-state/hooks';
import { darkTheme, lightTheme } from '~/theme.css';
import { isTest } from '~/utils/constants';
import { DrivePicker } from '~/utils/DrivePicker';
import { type Store, useModal, useOpenId, useTheme } from '~/utils/store';
import { isAFolder } from '~/utils/utils';
import * as styles from '../Tree/Tree.css';
import { useCreate } from '../Tree/treeMenuUtils';

export const AddMenu = ({
  wiki,
  file,
  files,
  popupState,
  type,
}: {
  wiki: DriveFile;
  file: DriveFile | undefined;
  files: DriveFile[];
  popupState: PopupState;
  type?: Store['menuType'];
}) => {
  const { openId, setOpenId } = useOpenId(type);
  const theme = useTheme();
  const closeMenus = (delay?: number) => {
    popupState.close();
    setOpenId(undefined, delay);
  };

  const { openModal } = useModal(openId, {
    onOpen: () => {
      closeMenus();
    },
  });
  const create = useCreate({ files, wikiId: wiki.id });

  return (
    <>
      <Menu
        {...bindMenu(popupState)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transitionDuration={{
          enter: isTest ? 0 : 200,
          exit: 0,
        }}
        onClose={() => {
          closeMenus(10);
        }}
        className={cx({
          [darkTheme]: theme === 'dark',
          [lightTheme]: theme === 'light',
        })}
        slotProps={{
          paper: {
            className: styles.menu,
          },
        }}
      >
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => openModal('document')}
        >
          <img
            {...(DEV && {
              referrerPolicy: 'no-referrer',
            })}
            src="https://www.gstatic.com/images/branding/product/1x/docs_48dp.png"
            className={styles.menuImage}
            alt="Document"
            aria-hidden="true"
          />
          Document
        </MenuItem>

        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => openModal('sheet')}
        >
          <img
            className={styles.menuImage}
            src="https://www.gstatic.com/images/branding/product/1x/sheets_48dp.png"
            alt="Sheet"
            aria-hidden="true"
          />
          Sheet
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => openModal('slide')}
        >
          <img
            className={styles.menuImage}
            src="https://www.gstatic.com/images/branding/product/1x/slides_48dp.png"
            alt="Slide"
            aria-hidden="true"
          />
          Slide
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => openModal('form')}
        >
          <img
            className={styles.menuImage}
            src="https://www.gstatic.com/images/branding/product/1x/forms_48dp.png"
            alt="Form"
            aria-hidden="true"
          />
          Form
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => openModal('folder')}
        >
          <CreateNewFolderOutlined className={styles.menuIcon} style={{ fontSize: 22 }} />
          Folder
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => openModal('link')}
        >
          <AddLinkOutlined className={styles.menuIcon} style={{ fontSize: 24 }} />
          Link
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={async () => {
            closeMenus();
            await DrivePicker.load();
            DrivePicker.generatePicker({
              type: 'file',
              wiki,
              onAction: (data) => {
                if (data.action !== 'picked') return;
                const pickerFile = data.docs[0];
                if (!pickerFile) throw new Error('file not defined');
                const parents = isAFolder(file) ? ([file.id] satisfies [string]) : file?.parents;
                if (!parents) throw new Error('parents not defined');
                create({
                  name: pickerFile.name,
                  mimeType: mimeTypes.shortcut,
                  parents,
                  shortcutDetails: {
                    targetId: pickerFile.id,
                    targetMimeType: pickerFile.mimeType,
                  },
                });
              },
            });
            void DrivePicker.open('file');
          }}
        >
          <AddToDrive style={{ fontSize: 22 }} className={styles.menuIcon} />
          Shortcut
        </MenuItem>
      </Menu>
    </>
  );
};
